/**
 * The Pick'em setup step of the create-pool dialog (ISSUE-1303). Mounted by
 * `features/createPool/CreatePoolTypeSetupStep.tsx` with this exact props
 * shape, after the league is chosen.
 *
 * The league's starting values (week rounds for NFL and college football,
 * day rounds elsewhere), whether it has spreads, the rules text and any field
 * errors all come from `usePickemSetupPreview`. The draft is seeded once per
 * league; the Commissioner changes it from there. Create sits in the dialog
 * footer, which lists whatever `pickemSetupMissingItems` says is missing.
 */
import { usePickemSetupPreview } from "@sportsmet/contest-sdk/private";
import { InlineBanner, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useEffect } from "react";

import {
  PICKEM_SETUP_FAILED,
  PICKEM_SETUP_LOADING,
  PICKEM_SETUP_NO_LEAGUE,
  PICKEM_SETUP_RETRY,
} from "./pickemCopy";
import {
  buildPickemSetupPayload,
  needsPickemSeed,
  pickemDraftFromSetup,
  type typePickemSetupDraft,
} from "./pickemCreateSetupDraft";
import { PickemRulesPreview } from "./PickemRulesLines";
import { PickemSettingsFields } from "./PickemSettingsFields";

const DEFAULTS_STALE_TIME_MS = 5 * 60 * 1000;

export type typePickemCreateSetupStepProps = {
  sportKey: string | null;
  leagueKey: string | null;
  setup: typePickemSetupDraft;
  onChangeSetup: (next: typePickemSetupDraft) => void;
};

export const PickemCreateSetupStep = (
  props: typePickemCreateSetupStepProps,
): ReactNode => {
  const { setup, onChangeSetup } = props;
  const leagueKey = props.leagueKey ?? "";

  const defaultsQuery = usePickemSetupPreview(
    { leagueKey },
    { enabled: leagueKey !== "", staleTime: DEFAULTS_STALE_TIME_MS },
  );
  const defaults = defaultsQuery.data?.data ?? null;

  // Seed once per league. A league change after the fact starts over.
  const shouldSeed = needsPickemSeed({
    draft: setup,
    leagueKey: props.leagueKey,
  });
  useEffect(() => {
    if (!shouldSeed || defaults === null) return;
    onChangeSetup(
      pickemDraftFromSetup({
        setup: defaults.defaults,
        leagueKey: props.leagueKey,
      }),
    );
  }, [shouldSeed, defaults, props.leagueKey, onChangeSetup]);

  const payload = buildPickemSetupPayload(setup);
  const rulesQuery = usePickemSetupPreview(
    { leagueKey, setup: payload },
    {
      enabled: leagueKey !== "" && payload !== null && !shouldSeed,
      // Keeps the rules text on screen while a change is re-previewed.
      placeholderData: (previous) => previous,
    },
  );
  const rules = rulesQuery.data?.data ?? null;

  let body: ReactNode;
  if (props.leagueKey === null) {
    body = <InlineBanner message={PICKEM_SETUP_NO_LEAGUE} />;
  } else if (defaultsQuery.isError || Boolean(defaultsQuery.data?.error)) {
    body = (
      <div data-testid="pickem-setup-failed">
        <InlineBanner
          variant="warning"
          message={PICKEM_SETUP_FAILED}
          action={{
            label: PICKEM_SETUP_RETRY,
            onAction: () => {
              void defaultsQuery.refetch();
            },
          }}
        />
      </div>
    );
  } else if (defaults === null || shouldSeed) {
    body = (
      <div
        className="flex flex-col gap-3"
        aria-label={PICKEM_SETUP_LOADING}
        data-testid="pickem-setup-loading"
      >
        <Skeleton shape="card" label={PICKEM_SETUP_LOADING} />
        <Skeleton shape="card" label={PICKEM_SETUP_LOADING} />
      </div>
    );
  } else {
    body = (
      <>
        <PickemSettingsFields
          draft={setup}
          onChange={onChangeSetup}
          hasSpreads={defaults.hasSpreads}
          fieldErrors={payload === null ? [] : (rules?.fieldErrors ?? [])}
        />
        <PickemRulesPreview
          isDraftComplete={payload !== null}
          rulesLines={rules?.rulesLines ?? null}
        />
      </>
    );
  }

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="pickem-create-setup-step"
      data-league-key={props.leagueKey ?? ""}
    >
      {body}
    </div>
  );
};

/**
 * The Review step's rules text for a Pick'em pool (ISSUE-1303), so the
 * Commissioner reads what players will see right before Create, not only on
 * the setup step. The same preview call as the setup step, so it is usually
 * already cached.
 */
export const PickemCreateReviewRules = (props: {
  leagueKey: string | null;
  setup: typePickemSetupDraft;
}): ReactNode => {
  const leagueKey = props.leagueKey ?? "";
  const payload = buildPickemSetupPayload(props.setup);
  const rulesQuery = usePickemSetupPreview(
    { leagueKey, setup: payload },
    { enabled: leagueKey !== "" && payload !== null },
  );
  if (leagueKey === "") return null;
  if (payload !== null && rulesQuery.isLoading) {
    return <Skeleton shape="card" label={PICKEM_SETUP_LOADING} />;
  }
  return (
    <div data-testid="pickem-review-rules">
      <PickemRulesPreview
        isDraftComplete={payload !== null}
        rulesLines={rulesQuery.data?.data?.rulesLines ?? null}
      />
    </div>
  );
};
